import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import InputNota from "./pages/InputNota";
import Produk from "./pages/Produk";
import Counter from "./pages/Counter";
import Laporan from "./pages/Laporan";
import KelolaUser from "./pages/KelolaUser";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import ConnectionStatus from "./components/ConnectionStatus";
import { startAutoSync } from "./utils/syncManager";
import { initDB } from "./utils/offlineDb";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with interceptor
export const axiosInstance = axios.create();

axiosInstance.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);

  useEffect(() => {
    checkAuth();
    // Initialize IndexedDB
    initDB().catch(console.error);
  }, []);

  // Setup auto-sync when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      const token = localStorage.getItem("token");
      
      const cleanup = startAutoSync(
        token,
        // onSyncStart
        () => {
          setIsSyncing(true);
          toast.info("Menyinkronkan data...");
        },
        // onSyncComplete
        (result) => {
          setIsSyncing(false);
          if (result.success) {
            if (result.synced > 0) {
              toast.success(`Berhasil mengirim ${result.synced} nota ke database!`);
            }
            if (result.failed > 0) {
              toast.warning(`${result.failed} nota gagal dikirim`);
            }
          } else if (result.error) {
            toast.error("Sinkronisasi gagal. Akan dicoba lagi.");
          }
        }
      );

      return cleanup;
    }
  }, [isAuthenticated, user]);

  const checkAuth = async () => {
    const token = localStorage.getItem("token");
    if (token) {
      try {
        const response = await axiosInstance.get(`${API}/auth/me`);
        setUser(response.data);
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem("token");
        setIsAuthenticated(false);
      }
    }
    setLoading(false);
  };

  const handleLogin = (token, userData) => {
    localStorage.setItem("token", token);
    setUser(userData);
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setIsAuthenticated(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="text-xl text-gray-600">Memuat...</div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      {isAuthenticated && <ConnectionStatus isSyncing={isSyncing} />}
      <BrowserRouter>
        <Routes>
          <Route
            path="/login"
            element={
              isAuthenticated ? (
                <Navigate to="/" replace />
              ) : (
                <Login onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              isAuthenticated ? (
                <Dashboard user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/nota"
            element={
              isAuthenticated ? (
                <InputNota user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/produk"
            element={
              isAuthenticated ? (
                <Produk user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/counter"
            element={
              isAuthenticated ? (
                <Counter user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/laporan"
            element={
              isAuthenticated ? (
                <Laporan user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
          <Route
            path="/users"
            element={
              isAuthenticated && user?.role === "admin" ? (
                <KelolaUser user={user} onLogout={handleLogout} />
              ) : (
                <Navigate to="/" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
